import { useEffect, useState } from 'react';

import { Box, Grid, Skeleton, Typography } from '@mui/material';
import { Card, CardBody, CardTitle } from '@apigrowfinance/pbolt-card';
import { ChipButton } from '@apigrowfinance/pbolt-chip-button';
import Icon, { SizeVariant } from '@apigrowfinance/pbolt-icon';
import { MapStrikethrough } from '@apigrowfinance/pbolt-icons';
import { Restricted } from '@apigrowfinance/pbolt-permission-provider';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';

import { AddressDetails } from '@clients/accountsClient';
import EditLocationDetails from '@components/locations/EditLocationDetails';
import MapWrapperContainer from '@components/map/MapWrapperContainer';
import LocationRow from '@pages/accountDetail/tabs/locations/LocationRow';
import getEnumTranslation from '@utils/getEnumTranslation';
import { getGoogleMapsAPIKey } from '@utils/URLs';

type LocationsProps = {
    isLoading?: boolean;
    locations?: AddressDetails[];
    openAddLocationModal: () => void;
    refetch: () => void;
};

const Locations = ({
    isLoading,
    locations = [],
    openAddLocationModal,
    refetch,
}: LocationsProps) => {
    const { theme } = useTheme();
    const [selectedId, setSelectedId] = useState<number>(null);
    useEffect(() => {
        // Default selected, first active mailing address if none then first active, if none then first entry
        if (!selectedId && locations?.length > 0) {
            let validLocations = locations.filter(
                (location) => location?.active && location?.mailing_address,
            );
            if (validLocations?.length === 0) {
                validLocations = locations.filter(
                    (location) => location?.active,
                );
            }
            if (validLocations?.length === 0) {
                validLocations = [locations[0]];
            }
            setSelectedId(validLocations[0]?.id);
        }
    }, [locations, selectedId]);

    const [editLocationOpen, setEditLocationOpen] = useState(false);
    const activeBlockDetails: AddressDetails[] = [];
    const inactiveBlockDetails: AddressDetails[] = [];

    /**
     * Sort active and inactive into two separate arrays; so we can create blocks for them.
     * If an element is the mailing address make it the first item in list
     */
    locations?.forEach((location) => {
        location?.active
            ? location?.mailing_address
                ? activeBlockDetails.unshift(location)
                : activeBlockDetails.push(location)
            : inactiveBlockDetails.push(location);
    });

    const handleEditClick = () => setEditLocationOpen(true);

    return (
        <Card isTopRadiusBorder={false}>
            <CardBody direction="row" sx={{ justifyContent: 'end' }}>
                <Restricted to="create.location">
                    <ChipButton
                        onClick={openAddLocationModal}
                        label={getEnumTranslation('add')}
                        isLoading={isLoading}
                        variant="addMenu"
                        bordered
                    />
                </Restricted>
                <Restricted to="edit.location">
                    <ChipButton
                        onClick={handleEditClick}
                        label={getEnumTranslation('edit')}
                        variant="editMenu"
                        isLoading={isLoading}
                        bordered
                    />
                </Restricted>
            </CardBody>

            <CardTitle>
                {isLoading ? (
                    <Skeleton width={225} data-testid="location-title-loader" />
                ) : (
                    <FormattedMessage
                        id="Locations.addresses"
                        defaultMessage="Addresses"
                    />
                )}
            </CardTitle>

            <Grid container>
                <Grid item width="50%">
                    {isLoading && (
                        <LocationRow
                            title={
                                <Skeleton
                                    width={theme.spacing(9)}
                                    data-testid="location-block-loading"
                                />
                            }
                            locations={[
                                {
                                    active: true,
                                } as AddressDetails,
                            ]}
                            isLoading
                        />
                    )}
                    {!!activeBlockDetails?.length && (
                        <LocationRow
                            handleClick={(id) => setSelectedId(id)}
                            selectedId={selectedId}
                            locations={activeBlockDetails}
                            title={
                                <Typography variant="overline">
                                    {getEnumTranslation('active')}
                                </Typography>
                            }
                        />
                    )}
                    {!!inactiveBlockDetails?.length && (
                        <LocationRow
                            handleClick={(id) => setSelectedId(id)}
                            locations={inactiveBlockDetails}
                            selectedId={selectedId}
                            title={
                                <Typography variant="overline">
                                    {getEnumTranslation('inactive')}
                                </Typography>
                            }
                        />
                    )}
                </Grid>
                <Grid item width="50%" minHeight={765}>
                    {getGoogleMapsAPIKey() != '' ? (
                        <Box height={theme.spacing(74.75)}>
                            <MapWrapperContainer
                                idToCenterOn={selectedId?.toString()}
                                markerAddresses={activeBlockDetails.concat(
                                    inactiveBlockDetails,
                                )}
                                disableControls
                                showLabels
                                sxProps={{
                                    borderRadius: theme.shape.borderRadius,
                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    borderColor: theme.palette.grey[200],
                                }}
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                backgroundColor: 'background.paper',
                                height: '100%',
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: '100%',
                                }}>
                                <Icon
                                    sizeVariant={SizeVariant.Medium}
                                    avatarProps={{
                                        sx: {
                                            color: 'text.secondary',
                                        },
                                    }}>
                                    <MapStrikethrough />
                                </Icon>

                                <Typography
                                    variant="overline"
                                    sx={{
                                        fontSize: (theme) =>
                                            theme.typography.overline.fontSize,
                                        mt: 2,
                                        mb: 1,
                                    }}>
                                    <FormattedMessage
                                        id="MapPanel.title"
                                        defaultMessage="No map available at this time."
                                    />
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: (theme) =>
                                            theme.typography.body2.fontSize,
                                    }}>
                                    <FormattedMessage
                                        id="MapPanel.desc"
                                        defaultMessage="We're working on it, check back soon."
                                    />
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
            {editLocationOpen && (
                <EditLocationDetails
                    locations={[...activeBlockDetails]}
                    setEditLocationOpen={setEditLocationOpen}
                    editLocationOpen={editLocationOpen}
                    refetch={refetch}
                />
            )}
        </Card>
    );
};
export default Locations;
